import type { ActionFunctionArgs } from '~/types/route-args';
import { data as routerData } from 'react-router';
import { z } from 'zod';
import { createConversationMessage } from '~/modules/chat/api/create-conversation-message.server';
import {
    MAX_NUM_ATTACHMENTS,
    validateChatUploadFile,
} from '~/modules/chat/utils/validation';

export const CREATE_CONVERSATION_MESSAGE_API =
    '/remix-api/chat/create-conversation-message';

const FORM_DATA_SCHEMA = z
    .object({
        conversationID: z.coerce.number().int().positive(),
        /**
         * The ID used to identify optimistic messages.
         */
        clientMessageID: z.string().min(1),
        professionalID: z.coerce.number().int().positive().nullish(),
        message: z.string().trim().nullish(),
        attachments: z
            .array(
                z.instanceof(File).refine(
                    (file) => {
                        const result = validateChatUploadFile(file);
                        return result.valid;
                    },
                    { message: 'Invalid file' },
                ),
            )
            .max(MAX_NUM_ATTACHMENTS)
            .nullish(),
        replyToID: z.coerce.number().int().positive().nullish(),
    })
    .refine(
        ({ message, attachments, replyToID }) => {
            return !!message || !!attachments?.length || !!replyToID;
        },
        {
            message:
                'Message, attachments, or a forwarded message are required',
        },
    );

/**
 * API endpoint to create a message in an enquiry conversation.
 * Use with the `useConversation` hook.
 * @see {@link packages/web/app/modules/chat/context/ConversationContext.tsx}
 */
export const action = async ({ context, request }: ActionFunctionArgs) => {
    const { logger } = context;
    const formData = await request.formData();
    const conversationID = formData.get('conversationID');
    const clientMessageID = formData.get('clientMessageID');
    const professionalID = formData.get('professionalID');
    const message = formData.get('message');
    const attachments = formData.getAll('attachments');
    const replyToID = formData.get('replyToID');
    const result = FORM_DATA_SCHEMA.safeParse({
        conversationID,
        clientMessageID,
        professionalID,
        message,
        attachments,
        replyToID,
    });

    if (!result.success) {
        return routerData(null, { status: 400 });
    }

    try {
        const response = await createConversationMessage({
            context,
            conversationID: result.data.conversationID,
            professionalID: result.data.professionalID,
            message: result.data.message ?? undefined,
            attachments: result.data.attachments ?? undefined,
            replyToID: result.data.replyToID ?? undefined,
        });

        if (!response) {
            return routerData(null, { status: 500 });
        }

        let statusCode = 200;
        if (
            response.__typename === 'NotAuthorizedError' ||
            response.__typename === 'NotFoundError'
        ) {
            statusCode = 403;
        } else if (response.__typename === 'UserError') {
            statusCode = 400;
        } else if (response.__typename === 'ServerError') {
            statusCode = 500;
        }

        return routerData(
            {
                response,
                clientMessageID: result.data.clientMessageID,
            },
            { status: statusCode },
        );
    } catch (error) {
        logger.error(`Error in ${CREATE_CONVERSATION_MESSAGE_API}`, {
            error,
            data: result.data,
        });
        return routerData(null, { status: 500 });
    }
};
